/**
 * Cambio de correo de una persona en TODAS las capas: persona, cuenta de acceso (auth +
 * usuarios) y, si hace falta, la vinculación con la cuenta que el cliente creó en el app.
 *
 * Las pantallas de Personas escribían solo `personas.email`: la cuenta seguía con el correo
 * viejo y el cliente no podía entrar ni recuperar su contraseña con el nuevo. Toda edición
 * de correo pasa por aquí ANTES del UPDATE de la persona; si la edge function lo rechaza,
 * no se guarda nada y la persona no queda desalineada de su cuenta.
 *
 * La edge function `update-user-email` autoriza por cuenta (el correo de acceso de un
 * cliente lo cambia quien gestiona Usuarios Clientes o Cobranza, no cualquiera que edite
 * Compradores) y la RPC `cambiar_correo_cuenta` aplica todo en una transacción.
 */
import { supabase } from "@/integrations/supabase/client";
import { extractEdgeFunctionError } from "@/lib/edgeFunctionError";

export type EscenarioCorreo = "sin_cambio" | "renombrar" | "vincular" | "bloqueado";

export interface CuentaPlan {
  email: string;
  rol_id: number;
  rol_final?: number;
  /** Nombres de rol (la RPC los manda desde multi-rol; antes solo ids). */
  rol_nombre?: string | null;
  rol_final_nombre?: string | null;
  /** Multi-rol: roles que la cuenta destino recibe además del suyo al vincular. */
  roles_a_agregar?: Array<{ rol_id: number; nombre: string }>;
  id_persona?: number | null;
  ultimo_acceso: string | null;
}

export interface PlanCambioCorreo {
  escenario: EscenarioCorreo;
  motivo: string | null;
  email_nuevo: string;
  persona: { id: number; nombre: string; email: string | null; es_compradora: boolean; se_actualiza: boolean } | null;
  cuenta_actual: CuentaPlan | null;
  cuenta_destino: CuentaPlan | null;
  persona_cascaron: { id: number; nombre: string } | null;
  cuentas_a_desactivar: CuentaPlan[];
  aplicado: boolean;
  cambios?: string[];
}

export interface ResultadoCambioCorreo {
  /** El plan aplicado, o null si el correo no cambió. */
  plan: PlanCambioCorreo | null;
  /** Correo de la cuenta de acceso que quedó con el correo nuevo (para mandarle acceso). */
  cuentaAcceso: string | null;
}

/** El admin canceló la vinculación: la pantalla no debe mostrar error. */
export class CambioCorreoCancelado extends Error {
  constructor() {
    super("Cambio de correo cancelado");
    this.name = "CambioCorreoCancelado";
  }
}

export function normalizarCorreo(email: string | null | undefined): string {
  return (email ?? "").trim().toLowerCase();
}

export function hayCambioDeCorreo(anterior: string | null | undefined, nuevo: string | null | undefined): boolean {
  const n = normalizarCorreo(nuevo);
  return n !== "" && n !== normalizarCorreo(anterior);
}

/** Qué cuenta termina usando el correo nuevo, según el escenario. */
export function cuentaConCorreoNuevo(plan: PlanCambioCorreo | null): string | null {
  if (!plan) return null;
  if (plan.escenario === "vincular") return plan.cuenta_destino?.email ?? null;
  if (plan.escenario === "renombrar" && plan.cuenta_actual) return plan.email_nuevo;
  return null;
}

async function invocar(body: Record<string, unknown>): Promise<{ plan: PlanCambioCorreo; requiereVinculacion?: boolean }> {
  const { data, error } = await supabase.functions.invoke("update-user-email", { body });
  if (error) {
    // 409 trae el plan en el cuerpo (bloqueado / falta confirmar la vinculación).
    const ctx = (error as { context?: Response }).context;
    const cuerpo = await ctx?.clone?.().json?.().catch(() => null);
    if (cuerpo?.plan) {
      if (cuerpo.requiereVinculacion) return { plan: cuerpo.plan, requiereVinculacion: true };
      throw new Error(cuerpo.message ?? cuerpo.plan.motivo ?? "No se pudo cambiar el correo");
    }
    throw new Error(await extractEdgeFunctionError(error));
  }
  if (!data?.success) throw new Error(data?.message ?? "No se pudo cambiar el correo");
  return { plan: data.plan };
}

/**
 * `vista` = ruta de la pantalla que pide el cambio (`/admin/compradores`, …). La edge
 * function exige el permiso `cambiar_correo` sobre ella y, según la pantalla, decide qué
 * cuentas puede tocar.
 */
export function previsualizarCambioCorreo(vista: string, idPersona: number, emailNuevo: string) {
  return invocar({ vista, idPersona, newEmail: normalizarCorreo(emailNuevo), modo: "previsualizar" });
}

export function aplicarCambioCorreo(vista: string, idPersona: number, emailNuevo: string, vincular: boolean) {
  return invocar({ vista, idPersona, newEmail: normalizarCorreo(emailNuevo), modo: "aplicar", vincular });
}

/**
 * Sincroniza el correo de la persona en todas las capas. Llamar ANTES del UPDATE de la
 * persona. `confirmarVinculacion` recibe el plan y resuelve true si el admin acepta ligar
 * la cuenta del app a esta persona.
 */
/**
 * `update-user-email` rechazó el cambio porque la persona es compradora: el correo va en la
 * solicitud de autorización (`useAutorizacionCambioComprador`), no por la edge function.
 */
export function esErrorCorreoComprador(error: unknown): boolean {
  return error instanceof Error && error.message.includes("El correo de un comprador pasa por autorización");
}

export async function sincronizarCorreoPersona(
  vista: string,
  idPersona: number,
  emailAnterior: string | null | undefined,
  emailNuevo: string | null | undefined,
  confirmarVinculacion: (plan: PlanCambioCorreo) => Promise<boolean>,
): Promise<ResultadoCambioCorreo> {
  if (!hayCambioDeCorreo(emailAnterior, emailNuevo)) {
    return vincularCuentaConMismoCorreo(vista, idPersona, emailNuevo, confirmarVinculacion);
  }

  const { plan } = await previsualizarCambioCorreo(vista, idPersona, emailNuevo!);
  if (plan.escenario === "sin_cambio") return { plan: null, cuentaAcceso: null };
  if (plan.escenario === "bloqueado") throw new Error(plan.motivo ?? "No se puede usar ese correo");

  let vincular = false;
  if (plan.escenario === "vincular") {
    vincular = await confirmarVinculacion(plan);
    if (!vincular) throw new CambioCorreoCancelado();
  }

  const { plan: aplicado } = await aplicarCambioCorreo(vista, idPersona, emailNuevo!, vincular);
  return { plan: aplicado, cuentaAcceso: cuentaConCorreoNuevo(aplicado) };
}

/**
 * Multi-rol: la persona ya tiene el correo, pero la CUENTA con ese correo no está ligada a
 * ninguna persona (p. ej. el Super Admin joseramon.escobar@sozu.com y su ficha de
 * Representante Comercial). Al guardar sin cambiar el correo se ofrece vincularlas: la
 * cuenta queda ligada a esta persona y conserva su rol. Nunca bloquea el guardado: si no hay
 * nada que vincular, falta permiso o el admin cancela, sigue como antes.
 */
async function vincularCuentaConMismoCorreo(
  vista: string,
  idPersona: number,
  email: string | null | undefined,
  confirmarVinculacion: (plan: PlanCambioCorreo) => Promise<boolean>,
): Promise<ResultadoCambioCorreo> {
  const sinCambio: ResultadoCambioCorreo = { plan: null, cuentaAcceso: null };
  const correo = normalizarCorreo(email);
  if (!correo) return sinCambio;

  let plan: PlanCambioCorreo;
  try {
    ({ plan } = await previsualizarCambioCorreo(vista, idPersona, correo));
  } catch {
    return sinCambio; // sin permiso `cambiar_correo` o pantalla sin alcance: no es un error del guardado
  }
  const destino = plan.cuenta_destino;
  // Solo el caso limpio: cuenta sin persona y nada que dar de baja. Si la persona tuviera otra
  // cuenta con otro correo (desalineada), no se "realinea" sin un cambio explícito de correo.
  if (
    plan.escenario !== "vincular" || !destino || destino.id_persona != null ||
    plan.cuentas_a_desactivar.length > 0
  ) {
    return sinCambio;
  }
  if (!(await confirmarVinculacion(plan))) return sinCambio;

  const { plan: aplicado } = await aplicarCambioCorreo(vista, idPersona, correo, true);
  return { plan: aplicado, cuentaAcceso: null };
}

